/**
 * ProfileCard — the Home profile panel: avatar, display name, @handle and a gold
 * referral badge (Frame 1 «Главная»).
 *
 * Reads the {@link Me} payload; presentation only.
 */
import { displayName, type Me } from "@entities/user";
import { formatBadgeCount } from "@shared/lib/format";
import { useState } from "react";

import styles from "./ProfileCard.module.css";

export interface ProfileCardProps {
  readonly me: Me;
  /** Optional handler for the referral indicator — makes it a tappable link to «Рефералы». */
  readonly onReferralsClick?: () => void;
}

function AvatarFallback(): JSX.Element {
  return (
    <svg
      className={styles.avatarFallback}
      width="27"
      height="27"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </svg>
  );
}

export function ProfileCard({
  me,
  onReferralsClick,
}: ProfileCardProps): JSX.Element {
  const name = displayName(me);
  const [imgFailed, setImgFailed] = useState(false);
  const showImg = me.avatar_url.length > 0 && !imgFailed;
  return (
    <div className={styles.card}>
      <div className={styles.avatar} aria-hidden="true">
        {showImg ? (
          <img
            src={me.avatar_url}
            alt=""
            className={styles.avatarImg}
            referrerPolicy="no-referrer"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <AvatarFallback />
        )}
      </div>
      <div className={styles.identity}>
        <span className={styles.name}>{name}</span>
        {me.username ? (
          <span className={styles.handle}>@{me.username}</span>
        ) : null}
      </div>
      <button
        type="button"
        className={styles.referrals}
        title="Приглашено друзей"
        aria-label="Открыть «Рефералы»"
        onClick={onReferralsClick}
        disabled={onReferralsClick === undefined}
      >
        <span className={styles.referralsCount}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M16 21v-1.5a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4V21" />
            <circle cx="9.5" cy="7" r="3.5" />
            <path d="M21 21v-1.5a4 4 0 0 0-3-3.85" />
            <path d="M16 3.5a4 4 0 0 1 0 7" />
          </svg>
          {formatBadgeCount(me.referrals_count)}
        </span>
        <span className={styles.referralsLabel}>рефералов</span>
      </button>
    </div>
  );
}
